import Papa from "papaparse";

export const CSV_HEADER_SCAN_ROWS = 25;
const CSV_EXPORT_GUIDANCE =
  "Please export the data from the Clean Sweeps tool exactly and do not change the first 6 rows.";
export const CSV_MISSING_COLUMNS_MESSAGE =
  `CSV is missing required columns. ${CSV_EXPORT_GUIDANCE}`;

export function detectEmailKey(headers) {
  const H = (headers || []).map((h) => String(h).toLowerCase());
  const targets = ["email", "customer_email", "e-mail"];
  for (const t of targets) {
    const i = H.indexOf(t);
    if (i >= 0) return headers[i];
  }
  const j = H.findIndex((h) => h.includes("email"));
  return j >= 0 ? headers[j] : "";
}

export function detectEntriesKey(headers) {
  const H = (headers || []).map((h) => String(h).toLowerCase());
  const targets = ["entriestotal", "entries_total", "entries", "entry_total", "tickets"];
  for (const t of targets) {
    const i = H.indexOf(t);
    if (i >= 0) return headers[i];
  }
  const j = H.findIndex((h) => h.includes("entries"));
  return j >= 0 ? headers[j] : "";
}

export function detectNameKey(headers) {
  const H = (headers || []).map((h) => String(h).toLowerCase());
  const exact = ["name", "customer_name", "full_name"];
  for (const t of exact) {
    const i = H.indexOf(t);
    if (i >= 0) return headers[i];
  }
  if (H.includes("first_name") || H.includes("last_name")) return "__first_last__";
  return "";
}

export function inspectCsvCompatibility(file, { requireDataUnderHeader = false } = {}) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: false,
      preview: CSV_HEADER_SCAN_ROWS,
      skipEmptyLines: false,
      worker: false,
      dynamicTyping: false,
      complete: (results) => {
        const rows = Array.isArray(results?.data) ? results.data : [];
        const structure = findCompatibleCsvHeader(rows);

        if (!structure) {
          reject(new Error(CSV_MISSING_COLUMNS_MESSAGE));
          return;
        }

        if (requireDataUnderHeader && !structure.hasDataUnderHeader) {
          reject(
            new Error(
              `Required columns were found on row ${structure.headerRowNumber}, but no data was found underneath them in the first ${CSV_HEADER_SCAN_ROWS} rows. ${CSV_EXPORT_GUIDANCE}`
            )
          );
          return;
        }

        resolve(structure);
      },
      error: (err) => reject(err || new Error("Failed to inspect CSV.")),
    });
  });
}

export function buildCompatibilityMessage() {
  return "CSV compatibility check passed.";
}

export async function prepareCsvSourceForParsing(file) {
  const structure = await inspectCsvCompatibility(file);

  if (structure.headerRowIndex <= 0) {
    return {
      source: file,
      useWorker: true,
      exportMetadata: structure.exportMetadata,
    };
  }

  const rawText = await readFileText(file);

  return {
    source: stripLeadingCsvLines(rawText, structure.headerRowIndex),
    useWorker: false,
    exportMetadata: structure.exportMetadata,
  };
}

export function extractSweepstakesMetadataFromRow(row) {
  if (!row || typeof row !== "object") return createEmptyExportMetadata();

  return {
    sweepstakesId: findRowValueByCandidates(row, [
      "sweepstakesid",
      "sweepstakeid",
      "promotionid",
      "giveawayid",
    ]),
    sweepstakesName: findRowValueByCandidates(row, [
      "sweepstakesname",
      "sweepstakename",
      "promotionname",
      "giveawayname",
    ]),
    sweepstakesStartDate: findRowValueByCandidates(row, [
      "sweepstakesstartdate",
      "promotionstartdate",
      "startdate",
      "startsat",
    ]),
    sweepstakesEndDate: findRowValueByCandidates(row, [
      "sweepstakesenddate",
      "promotionenddate",
      "enddate",
      "endsat",
      "closedate",
      "closingdate",
    ]),
  };
}

export function mergeSweepstakesMetadata(primary, secondary) {
  const first = normalizeExportMetadata(primary);
  const second = normalizeExportMetadata(secondary);

  return {
    sweepstakesId: first.sweepstakesId || second.sweepstakesId,
    sweepstakesName: first.sweepstakesName || second.sweepstakesName,
    sweepstakesStartDate: first.sweepstakesStartDate || second.sweepstakesStartDate,
    sweepstakesEndDate: first.sweepstakesEndDate || second.sweepstakesEndDate,
  };
}

function findCompatibleCsvHeader(rows) {
  for (let i = 0; i < rows.length; i++) {
    const headers = normalizeCsvPreviewRow(rows[i]);
    if (!headers.length || !rowHasAnyValue(headers)) continue;

    const emailKey = detectEmailKey(headers);
    const entriesKey = detectEntriesKey(headers);
    const nameKey = detectNameKey(headers);

    if (!emailKey || !entriesKey) continue;

    const emailIndex = headers.indexOf(emailKey);
    const entriesIndex = headers.indexOf(entriesKey);
    const hasDataUnderHeader = rows
      .slice(i + 1)
      .map(normalizeCsvPreviewRow)
      .some((row) => rowHasValueAtIndexes(row, [emailIndex, entriesIndex]));

    return {
      headerRowIndex: i,
      headerRowNumber: i + 1,
      headers,
      emailKey,
      entriesKey,
      nameKey,
      hasDataUnderHeader,
      exportMetadata: extractSweepstakesMetadataFromPreviewRows(rows.slice(0, i)),
    };
  }

  return null;
}

function normalizeCsvPreviewRow(row) {
  if (!Array.isArray(row)) return [];
  return row.map((value) => String(value ?? "").trim());
}

function rowHasAnyValue(row) {
  return (row || []).some((value) => hasValue(value));
}

function rowHasValueAtIndexes(row, indexes) {
  return (indexes || []).some((index) => index >= 0 && hasValue(row?.[index]));
}

function hasValue(v) {
  return String(v || "").trim() !== "";
}

function findRowValueByCandidates(row, candidates) {
  const normalizedRow = new Map(
    Object.keys(row || {}).map((key) => [normalizeLabel(key), cleanMetadataValue(row[key])])
  );

  for (const candidate of candidates) {
    const value = normalizedRow.get(normalizeLabel(candidate));
    if (value) return value;
  }

  return "";
}

function extractSweepstakesMetadataFromPreviewRows(rows) {
  const metadata = createEmptyExportMetadata();

  for (const row of rows || []) {
    const cells = normalizeCsvPreviewRow(row).filter(Boolean);
    if (!cells.length) continue;

    const rowAssignments = extractMetadataAssignmentsFromCells(cells);
    metadata.sweepstakesId ||= rowAssignments.sweepstakesId;
    metadata.sweepstakesName ||= rowAssignments.sweepstakesName;
    metadata.sweepstakesStartDate ||= rowAssignments.sweepstakesStartDate;
    metadata.sweepstakesEndDate ||= rowAssignments.sweepstakesEndDate;
  }

  return metadata;
}

function extractMetadataAssignmentsFromCells(cells) {
  const metadata = createEmptyExportMetadata();

  for (let i = 0; i < cells.length; i++) {
    const cell = String(cells[i] || "").trim();
    if (!cell) continue;

    const inline = parseInlineMetadataCell(cell);
    if (inline) {
      metadata[inline.key] ||= inline.value;
      continue;
    }

    const nextValue = cleanMetadataValue(findNextValueCell(cells, i + 1));
    const targetKey = detectMetadataKey(cell);
    if (targetKey && nextValue) {
      metadata[targetKey] ||= nextValue;
    }
  }

  return metadata;
}

function parseInlineMetadataCell(cell) {
  const match = String(cell || "").match(/^(.*?)(?::|\||\s-\s)(.+)$/);
  if (!match) return null;

  const label = match[1];
  const value = cleanMetadataValue(match[2]);
  const key = detectMetadataKey(label);
  if (!key || !value) return null;

  return { key, value };
}

function findNextValueCell(cells, startIndex) {
  for (let i = startIndex; i < (cells || []).length; i++) {
    if (hasValue(cells[i])) return cells[i];
  }
  return "";
}

function detectMetadataKey(label) {
  const normalized = normalizeLabel(label);

  if (
    [
      "sweepstakesid",
      "sweepstakeid",
      "promotionid",
      "giveawayid",
    ].includes(normalized)
  ) {
    return "sweepstakesId";
  }

  if (
    [
      "sweepstakesname",
      "sweepstakename",
      "promotionname",
      "giveawayname",
    ].includes(normalized)
  ) {
    return "sweepstakesName";
  }

  if (
    [
      "sweepstakesstartdate",
      "promotionstartdate",
      "startdate",
      "startsat",
    ].includes(normalized)
  ) {
    return "sweepstakesStartDate";
  }

  if (
    [
      "sweepstakesenddate",
      "promotionenddate",
      "enddate",
      "endsat",
      "closedate",
      "closingdate",
    ].includes(normalized)
  ) {
    return "sweepstakesEndDate";
  }

  return "";
}

function createEmptyExportMetadata() {
  return {
    sweepstakesId: "",
    sweepstakesName: "",
    sweepstakesStartDate: "",
    sweepstakesEndDate: "",
  };
}

function normalizeExportMetadata(metadata) {
  return {
    sweepstakesId: cleanMetadataValue(metadata?.sweepstakesId),
    sweepstakesName: cleanMetadataValue(metadata?.sweepstakesName),
    sweepstakesStartDate: cleanMetadataValue(metadata?.sweepstakesStartDate),
    sweepstakesEndDate: cleanMetadataValue(metadata?.sweepstakesEndDate),
  };
}

function cleanMetadataValue(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function stripLeadingCsvLines(text, linesToRemove) {
  if (!linesToRemove) return text;

  let index = 0;
  let removed = 0;

  while (index < text.length && removed < linesToRemove) {
    const nextLineBreak = text.indexOf("\n", index);
    if (nextLineBreak === -1) return "";
    index = nextLineBreak + 1;
    removed += 1;
  }

  return text.slice(index);
}

function readFileText(file) {
  if (file && typeof file.text === "function") {
    return file.text();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed to read CSV."));
    reader.readAsText(file);
  });
}

function normalizeLabel(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}
